// ✅ src/pages/OrderManagement/RateApprove.jsx
// FULL FILE — sidebar updated like PurchaseOrderListingComponent
// Kept existing Rate Approval features:
// 1) Table-only scrollbar
// 2) Reduced work-details column width
// 3) Wider Remarks + Date inputs
// 4) Date inputs show yyyy-MM-dd but save as dd/MM/yyyy
// 5) Save / Approve / Print / Select All / Clear All kept intact

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSave,
  FaPrint,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

const API = "https://nissi-india.com/api/rateApprove";

// dd/MM/yyyy -> yyyy-MM-dd (for <input type="date">)
const dmyToIso = (dmy) => {
  const s = String(dmy || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  const yyyy = m[3];
  return `${yyyy}-${mm}-${dd}`;
};

// yyyy-MM-dd -> dd/MM/yyyy (to send to SP)
const isoToDmy = (iso) => {
  const s = String(iso || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return "";
  const yyyy = m[1];
  const mm = m[2].padStart(2, "0");
  const dd = m[3].padStart(2, "0");
  return `${dd}/${mm}/${yyyy}`;
};

const RateApprovalComponent = () => {
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState("Rate Approval");

  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    const cid = Number(fromUrl || fromSession || 0);
    return Number.isFinite(cid) ? cid : 0;
  }, [location.search]);

  const withContractId = (path) => {
    if (!contractId) return path;

    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ") },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  const username = useMemo(() => {
    return (
      localStorage.getItem("username") ||
      localStorage.getItem("username") ||
      localStorage.getItem("UserName") ||
      ""
    );
  }, []);

  const [saveState, setSaveState] = useState("Save");
  const [tableData, setTableData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem.name);
    console.log(`Navigating to: ${menuItem.name}`);
  };

  const mapApiRowToUi = (r) => ({
    categoryName: r.fld_projectcategoryname ?? "",
    description: r.fld_work ?? "",
    unit: r.fld_unit ?? "",
    quantity: Number(r.fld_Qty ?? r.fld_qty ?? 0) || 0,
    boqRate: Number(r.fld_rate ?? 0) || 0,
    amount: Number(r.fld_amount ?? 0) || 0,

    // store dd/MM/yyyy like ASP; date inputs display ISO
    fromDate: r.fld_fromdate ?? "",
    toDate: r.fld_todate ?? "",

    remarks: r.fld_remarks ?? "",
    selected: false,

    preparedBy: r.fld_preparedby ?? "",
    authorizedBy: r.fld_autherizedby ?? "",
    approval: r.fld_approveby ?? "",

    fld_projectcategoryid: r.fld_projectcategoryid,
    fld_WorkId: r.fld_WorkId ?? r.fld_workid ?? r.fld_workinvolveditemid,
    fld_type: r.fld_type ?? "",
  });

  const loadGrid = async () => {
    setLoading(true);
    setError("");
    try {
      if (!contractId) {
        setError("contractId missing in URL");
        setTableData([]);
        setSaveState("Save");
        return;
      }

      const res = await axios.get(`${API}/grid`, { params: { contractId } });

      if (res.data?.success) {
        setSaveState(res.data?.mode || "Save");
        const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
        setTableData(rows.map(mapApiRowToUi));
      } else {
        setError(res.data?.message || "Failed to load");
        setTableData([]);
        setSaveState("Save");
      }
    } catch (e) {
      console.error("RateApprove load error:", e);
      setError("Server error");
      setTableData([]);
      setSaveState("Save");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const moduleSelect =
      sessionStorage.getItem("ModuleSelect") ||
      sessionStorage.getItem("moduleSelect") ||
      "ERP";

    if (String(moduleSelect).toUpperCase() !== "ERP") {
      window.location.href = "/ModuleHomePage";
      return;
    }

    loadGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const handleInputChange = (index, field, value) => {
    const newData = [...tableData];
    newData[index][field] = value;
    setTableData(newData);
  };

  const handleCheckboxChange = (index) => {
    const newData = [...tableData];
    newData[index].selected = !newData[index].selected;
    setTableData(newData);
  };

  const handleSave = async () => {
    if (saving) return;

    if (!contractId) return alert("contractId missing");
    if (!username) return alert("Username missing in sessionStorage");
    if (!tableData.length) return alert("No rows");

    setSaving(true);
    try {
      const payload = {
        contractId,
        username,
        mode: saveState,
        rows: tableData.map((r) => ({
          fld_projectcategoryid: r.fld_projectcategoryid,
          fld_WorkId: r.fld_WorkId,
          fld_unit: r.unit,
          fld_Qty: r.quantity,
          fld_rate: r.boqRate,
          fld_amount: r.amount,
          fld_fromdate: r.fromDate,
          fld_todate: r.toDate,
          fld_remarks: r.remarks,
          fld_type: r.fld_type,
        })),
      };

      const res = await axios.post(`${API}/submit`, payload);

      if (res.data?.success) {
        alert(res.data?.message || "Saved Successfully");
        loadGrid();
      } else {
        alert(res.data?.message || "Saved Failed");
      }
    } catch (e) {
      console.error("RateApprove save error:", e);
      alert("Saved Failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (printing) return;

    const selectedRows = tableData
      .filter((x) => x.selected)
      .map((r) => ({
        fld_projectcategoryname: r.categoryName,
        fld_work: r.description,
        fld_unit: r.unit,
        fld_Qty: r.quantity,
        fld_rate: r.boqRate,
        fld_amount: r.amount,
        fld_fromdate: r.fromDate,
        fld_todate: r.toDate,
        fld_remarks: r.remarks,
        fld_preparedby: r.preparedBy,
        fld_autherizedby: r.authorizedBy,
        fld_approvedby: r.approval,
        fld_type: r.fld_type || r.type || "",
      }));

    if (!selectedRows.length) {
      alert("Please select at least one row");
      return;
    }

    setPrinting(true);
    try {
      const res = await axios.post(`${API}/printData`, {
        contractId,
        selectedRows,
      });

      if (!res.data?.success) {
        alert(res.data?.message || "Print failed");
        return;
      }

      sessionStorage.setItem("RateApprovalPrint_payload", JSON.stringify(res.data));
      window.open("/OrderManagement/RateApprovalPrint", "_blank", "width=900,height=900,scrollbars=yes");
    } catch (e) {
      console.error("RateApprove print error:", e);
      alert("Print failed");
    } finally {
      setPrinting(false);
    }
  };

  const totalAmount = tableData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const selectedAmount = tableData
    .filter((x) => x.selected)
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                onClick={() => handleMenuItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  location.pathname === new URL(item.path, window.location.origin).pathname
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={`transition-all duration-300 min-h-screen ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            <div className="p-6">
              <div
                className={`backdrop-blur-md border rounded-2xl mb-6 ${
                  isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
                }`}
              >
                <div className="px-6 py-4">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                    <div>
                      <h1
                        className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <FaCheckCircle className="text-blue-500" />
                        RATE APPROVAL
                      </h1>
                      <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Rate Approval Management System
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        ContractId: {contractId} | Mode: {saveState} | Active Menu: {activeMenuItem}
                      </p>
                      {error ? (
                        <p className={`text-xs mt-1 ${isDark ? "text-red-300" : "text-red-600"}`}>{error}</p>
                      ) : null}
                      {loading ? (
                        <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</p>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        <FaSave />
                        {saving ? "Saving..." : "Save"}
                      </button>

                      <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        <FaCheckCircle />
                        Approve
                      </button>

                      <button
                        onClick={handlePrint}
                        disabled={printing || loading}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                      >
                        <FaPrint />
                        {printing ? "Printing..." : "Print"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`rounded-2xl shadow-2xl border overflow-hidden ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
                  <table className="w-full table-fixed">
                    <thead className="bg-blue-600 text-white sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[120px]">
                          Category Name
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[360px]">
                          Ongoing Project Nature Of Work Details
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[90px]">
                          Unit
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[110px]">
                          Quantity
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[110px]">
                          BOQ Rate
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[120px]">
                          Amount
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[150px]">
                          From Date
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[150px]">
                          To Date
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[220px]">
                          Remarks
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-blue-500 w-[90px]">
                          Select
                        </th>

                        <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider w-[120px]">
                          Prepared By
                        </th>
                      </tr>
                    </thead>

                    <tbody className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
                      {tableData.map((item, index) => (
                        <tr
                          key={index}
                          className={`transition-colors duration-200 align-top ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td
                            className={`px-3 py-4 text-sm border-r break-words ${
                              isDark ? "text-blue-300 border-gray-700" : "text-blue-600 border-gray-200"
                            }`}
                          >
                            <span className="font-medium">{item.categoryName}</span>
                          </td>

                          <td
                            className={`px-4 py-4 text-sm border-r ${
                              isDark ? "text-gray-200 border-gray-700" : "text-gray-700 border-gray-200"
                            }`}
                          >
                            <p className="leading-relaxed text-justify break-words">{item.description}</p>
                          </td>

                          <td
                            className={`px-3 py-4 text-sm font-medium border-r break-words ${
                              isDark ? "text-purple-300 border-gray-700" : "text-purple-600 border-gray-200"
                            }`}
                          >
                            {item.unit}
                          </td>

                          <td
                            className={`px-3 py-4 text-sm border-r ${
                              isDark ? "text-orange-300 border-gray-700" : "text-orange-600 border-gray-200"
                            }`}
                          >
                            {Number(item.quantity || 0).toFixed(2)}
                          </td>

                          <td
                            className={`px-3 py-4 text-sm font-semibold border-r ${
                              isDark ? "text-green-300 border-gray-700" : "text-green-600 border-gray-200"
                            }`}
                          >
                            {Number(item.boqRate || 0).toFixed(2)}
                          </td>

                          <td
                            className={`px-3 py-4 text-sm font-bold border-r ${
                              isDark ? "text-green-200 border-gray-700" : "text-green-700 border-gray-200"
                            }`}
                          >
                            {Number(item.amount || 0).toFixed(2)}
                          </td>

                          <td className={`px-3 py-4 border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="date"
                              value={dmyToIso(item.fromDate)}
                              onChange={(e) => handleInputChange(index, "fromDate", isoToDmy(e.target.value))}
                              className={`w-full min-w-[135px] px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className={`px-3 py-4 border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="date"
                              value={dmyToIso(item.toDate)}
                              onChange={(e) => handleInputChange(index, "toDate", isoToDmy(e.target.value))}
                              className={`w-full min-w-[135px] px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </td>

                          <td className={`px-3 py-4 border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="text"
                              value={item.remarks}
                              onChange={(e) => handleInputChange(index, "remarks", e.target.value)}
                              className={`w-full min-w-[200px] px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                isDark
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                              }`}
                              placeholder="Enter remarks"
                            />
                          </td>

                          <td className={`px-3 py-4 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={() => handleCheckboxChange(index)}
                              className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                          </td>

                          <td className={`px-3 py-4 text-sm break-words ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            {item.preparedBy}
                          </td>
                        </tr>
                      ))}

                      {!loading && tableData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={11}
                            className={`px-6 py-10 text-center text-sm ${
                              isDark ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            No Record Found
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div className={`${isDark ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"} px-6 py-4 border-t`}>
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
                    <div className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Total Records: {tableData.length} | Selected: {tableData.filter((item) => item.selected).length}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <div className={`text-sm font-semibold ${isDark ? "text-green-300" : "text-green-700"}`}>
                        Total Amount: ₹{totalAmount.toLocaleString("en-IN")}
                      </div>
                      <div className={`text-sm font-semibold ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                        Selected Amount: ₹{selectedAmount.toLocaleString("en-IN")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`mt-6 p-6 rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        const newData = [...tableData];
                        newData.forEach((x) => (x.selected = true));
                        setTableData(newData);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200"
                    >
                      Select All
                    </button>

                    <button
                      onClick={() => {
                        const newData = [...tableData];
                        newData.forEach((x) => (x.selected = false));
                        setTableData(newData);
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all duration-200"
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <button
                      onClick={handleSave}
                      disabled={saving || loading}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg transition-all duration-200"
                    >
                      Bulk Approve Selected
                    </button>

                    <button
                      className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200"
                      onClick={() => alert("Reject is UI only (no ASP backend action here).")}
                    >
                      Reject Selected
                    </button>

                    <button
                      className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all duration-200"
                      onClick={() => alert("Export is UI only (no ASP backend action here).")}
                    >
                      Export Selected
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default RateApprovalComponent;